/**
 * userProfilesApi — per-user profile reads and writes.
 *
 * Backs the `user_profiles` table (see migration 13). Currently used only
 * by the intro video gating logic, which needs to know whether a given
 * user has already seen the intro on the current South African calendar
 * day.
 *
 * Both functions log errors but never throw. A failure here must not block
 * the rest of the app — the worst case is that the intro video shows an
 * extra time, or not at all on a given day.
 */

import supabase from './supabaseClient';

// Detect the Postgres "relation does not exist" failure (error code 42P01),
// which means migration 13_user_profiles_intro_video.sql has not been applied
// to the live Supabase project. We surface this as a loud, distinct warning
// because the symptom (silent fall-through to "show video every login") is
// hard to debug without seeing this message.
function isMissingTableError(error) {
  if (!error) return false;
  if (error.code === '42P01') return true;
  return /relation .*user_profiles.* does not exist/i.test(error.message || '');
}

function warnMissingMigration(context) {
  console.warn(
    `[Inbox] user_profiles table is missing (${context}). ` +
    'Apply migration 13_user_profiles_intro_video.sql in the Supabase SQL ' +
    'editor for project yaivpbnutpmmcnrtuoiy and reload.'
  );
}

/**
 * Return the SAST date (YYYY-MM-DD) on which the user last saw the intro
 * video, or null if they have never seen it (or the read failed).
 */
export async function getIntroVideoLastPlayedDate(userId) {
  if (!userId) return null;
  const { data, error } = await supabase
    .from('user_profiles')
    .select('intro_video_last_played_date')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) {
    if (isMissingTableError(error)) {
      warnMissingMigration('getIntroVideoLastPlayedDate');
    } else {
      console.error('getIntroVideoLastPlayedDate failed:', error);
    }
    return null;
  }
  return data?.intro_video_last_played_date ?? null;
}

/**
 * Record that the user has now seen the intro video on the given SAST date.
 * Upserts the user's row in `user_profiles`. Caller is responsible for
 * passing today's SAST date as a YYYY-MM-DD string (see getTodaySASTDateString).
 */
export async function markIntroVideoPlayedToday(userId, sastDateString) {
  if (!userId || !sastDateString) return;
  const { error } = await supabase
    .from('user_profiles')
    .upsert(
      {
        user_id: userId,
        intro_video_last_played_date: sastDateString,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' }
    );
  if (error) {
    if (isMissingTableError(error)) {
      warnMissingMigration('markIntroVideoPlayedToday');
    } else {
      console.error('markIntroVideoPlayedToday failed:', error);
    }
  }
}

// ── Unified statistics bar preference (CLAUDE.md §37, migration 31) ─────────

/** The four statistics sets the Adviser can choose between. */
export const STATS_BAR_VIEWS = ['files', 'prep', 'referrals', 'reviews'];

/**
 * Return the user's saved statistics-bar view ('files' | 'prep' |
 * 'referrals' | 'reviews'), or null when there is no row, the value is
 * invalid, or the read failed (e.g. migration 31 not applied yet). The
 * caller falls back to 'files'. Never throws.
 */
export async function getStatsBarView(userId) {
  if (!userId) return null;
  const { data, error } = await supabase
    .from('user_profiles')
    .select('stats_bar_view')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) {
    console.warn('[Inbox] getStatsBarView failed — falling back to File folders:', error.message || error);
    return null;
  }
  const value = data?.stats_bar_view;
  return STATS_BAR_VIEWS.includes(value) ? value : null;
}

/**
 * Save the user's statistics-bar view. Upserts the user's own row (the
 * existing migration-13 insert/update-self RLS policies cover it) and
 * touches only this column plus updated_at. Returns true on success;
 * logs a warning and returns false on failure. Never throws — a failed
 * save only means the choice is not remembered next time.
 */
export async function saveStatsBarView(userId, view) {
  if (!userId || !STATS_BAR_VIEWS.includes(view)) return false;
  const { error } = await supabase
    .from('user_profiles')
    .upsert(
      { user_id: userId, stats_bar_view: view, updated_at: new Date().toISOString() },
      { onConflict: 'user_id' }
    );
  if (error) {
    console.warn('[Inbox] saveStatsBarView failed — choice kept for this session only:', error.message || error);
    return false;
  }
  return true;
}
